import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabase';
import { c, CAIRN_GLYPH_PATHS } from '../constants';

// Scoped to superadmin accounts only (App.js's requireMfaOrCommit decides
// who reaches this screen at all -- Bryan's explicit correction, 2026-10-04:
// requiring every clinic customer to install a third-party authenticator app
// is an unacceptable adoption barrier, not the intended design). App.js
// deliberately holds the hydrated profile/clinic/membership outside the main
// application until this gate confirms AAL2, so patient-fetch effects can't
// start behind the screen while a superadmin caller is still only AAL1.
export default function MfaGate({ onVerified, onLogout }) {
  const { t } = useTranslation();
  const [factor, setFactor] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [needsEnrollment, setNeedsEnrollment] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const onVerifiedRef = useRef(onVerified);
  const translateRef = useRef(t);

  useEffect(() => { onVerifiedRef.current = onVerified; }, [onVerified]);
  useEffect(() => { translateRef.current = t; }, [t]);

  useEffect(() => {
    let cancelled = false;

    const prepare = async () => {
      try {
        const { data: aal, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aalError) throw aalError;
        if (aal?.currentLevel === 'aal2') {
          if (!cancelled) onVerifiedRef.current();
          return;
        }

        const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
        if (factorsError) throw factorsError;
        const verifiedTotp = factors?.totp?.[0];
        if (verifiedTotp) {
          if (!cancelled) setFactor(verifiedTotp);
          return;
        }
        if (!cancelled) setNeedsEnrollment(true);
      } catch (err) {
        if (!cancelled) setError(err?.message || translateRef.current('mfa.setupError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    prepare();
    return () => { cancelled = true; };
  }, []);

  const beginEnrollment = async () => {
    if (loading || verifying) return;
    setLoading(true); setError('');
    try {
      // Re-read immediately before mutation in case another tab completed
      // enrollment after this screen's initial read.
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) throw factorsError;
      const verifiedTotp = factors?.totp?.[0];
      if (verifiedTotp) {
        setFactor(verifiedTotp);
        setNeedsEnrollment(false);
        return;
      }

      // An interrupted enrollment leaves an unusable unverified factor.
      // Remove only this caller's unverified TOTP factors before creating a
      // fresh QR code; verified factors are never removed here.
      const abandoned = (factors?.all || []).filter(item => (
        item.factor_type === 'totp' && item.status === 'unverified'
      ));
      for (const item of abandoned) {
        const { error: removeError } = await supabase.auth.mfa.unenroll({ factorId: item.id });
        if (removeError) throw removeError;
      }

      const { data: enrolled, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Cairn clinic dashboard',
        issuer: 'Cairn Pet Health',
      });
      if (enrollError) throw enrollError;
      if (!enrolled?.id || !enrolled?.totp?.qr_code) throw new Error('MFA enrollment was incomplete');
      setFactor({ id: enrolled.id });
      setEnrollment(enrolled.totp);
      setNeedsEnrollment(false);
    } catch (err) {
      setError(err?.message || t('mfa.setupError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async event => {
    event.preventDefault();
    const normalizedCode = code.replace(/\s/g, '');
    if (!factor?.id || !/^\d{6}$/.test(normalizedCode) || verifying) return;
    setVerifying(true); setError('');
    try {
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factor.id,
        code: normalizedCode,
      });
      if (verifyError) throw verifyError;
      const { data: aal, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalError) throw aalError;
      if (aal?.currentLevel !== 'aal2') throw new Error('Session did not reach AAL2');
      onVerifiedRef.current();
    } catch (err) {
      setError(err?.message || t('mfa.verifyError'));
      setVerifying(false);
    }
  };

  return (
    <main style={{ minHeight: '100vh', backgroundColor: c.fog, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ backgroundColor: c.white, borderRadius: 20, padding: 40, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(79,100,115,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <svg width="28" height="28" viewBox="0 0 128 128" aria-hidden="true">
            {CAIRN_GLYPH_PATHS.map((d, i) => <path key={i} d={d} fill={c.slate} />)}
          </svg>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.slate, letterSpacing: 2 }}>Cairn</div>
            <div style={{ fontSize: 9, color: c.moss, letterSpacing: 2, textTransform: 'uppercase' }}>PET HEALTH</div>
          </div>
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 600, color: c.slate, margin: '0 0 6px' }}>
          {(enrollment || needsEnrollment) ? t('mfa.enrollTitle') : t('mfa.challengeTitle')}
        </h1>
        <p style={{ fontSize: 13, color: c.textSoft, lineHeight: 1.5, margin: '0 0 20px' }}>
          {loading ? t('mfa.preparing') : (enrollment || needsEnrollment) ? t('mfa.enrollBody') : t('mfa.challengeBody')}
        </p>

        {enrollment && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <img
              src={enrollment.qr_code}
              alt={t('mfa.qrAlt')}
              width="200"
              height="200"
              style={{ display: 'block', margin: '0 auto 12px', maxWidth: '100%', height: 'auto' }}
            />
            <p style={{ fontSize: 12, color: c.textSoft, margin: '0 0 6px' }}>{t('mfa.manualEntry')}</p>
            <code style={{ display: 'block', padding: 10, overflowWrap: 'anywhere', borderRadius: 8, backgroundColor: c.fog, color: c.slate, fontSize: 13 }}>
              {enrollment.secret}
            </code>
          </div>
        )}

        {!loading && needsEnrollment && !factor?.id && (
          <button
            type="button"
            onClick={beginEnrollment}
            style={{ width: '100%', padding: 12, backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}
          >
            {t('mfa.beginSetup')}
          </button>
        )}

        {!loading && factor?.id && (
          <form onSubmit={handleVerify}>
            <label htmlFor="mfa-code" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>
              {t('mfa.codeLabel')}
            </label>
            <input
              id="mfa-code"
              value={code}
              onChange={event => setCode(event.target.value.replace(/[^0-9 ]/g, '').slice(0, 7))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              aria-describedby="mfa-code-hint"
              style={{ width: '100%', padding: '11px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 18, letterSpacing: 4, color: c.slate, boxSizing: 'border-box', outline: 'none', marginBottom: 6 }}
            />
            <p id="mfa-code-hint" style={{ fontSize: 11, color: c.textSoft, margin: '0 0 14px' }}>{t('mfa.codeHint')}</p>
            {error && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{error}</p>}
            <button
              type="submit"
              disabled={!/^\d{6}$/.test(code.replace(/\s/g, '')) || verifying}
              style={{ width: '100%', padding: 12, backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!/^\d{6}$/.test(code.replace(/\s/g, '')) || verifying) ? 0.5 : 1, marginBottom: 10 }}
            >
              {verifying ? t('mfa.verifying') : t('mfa.continue')}
            </button>
          </form>
        )}

        {error && (loading || !factor?.id) && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{error}</p>}
        <button
          type="button"
          onClick={onLogout}
          disabled={verifying}
          style={{ width: '100%', padding: 12, backgroundColor: c.white, color: c.textSoft, border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
        >
          {t('common.signOut')}
        </button>
      </div>
    </main>
  );
}
